import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useUser, useUserManager } from "@/tanstackQuery/queries";
import { UserRole } from "@/types/ApiResponses/User";
import {
  BalanceQueryKeys,
  MainDealersQueryKeys,
  SubDealersQueryKeys,
  AgentsQueryKeys,
} from "@/tanstackQuery/queries";
import { updateUsersBalance } from "@/api/users";
import { updateSubDealerBalance } from "@/api/subDealers";
import { updateDealerBalance } from "@/api/mainDealers";
import { updateAgentBalance } from "@/api/agents";

export const useUpdateBalance = ({
  type,
}: {
  type: "players" | "subDealers" | "mainDealers" | "agents";
}) => {
  const queryClient = useQueryClient();
  const user = useUser();
  const userManager = useUserManager();

  return useMutation({
    mutationFn:
      type === "players"
        ? updateUsersBalance
        : type === "subDealers"
        ? updateSubDealerBalance
        : type === "agents"
        ? updateAgentBalance
        : updateDealerBalance,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [BalanceQueryKeys.Transactions],
      });

      // The initiator often funds the change out of its own pool (an operator
      // topping up an agent, an agent topping up its dealer), so its own
      // balance moved too. Refetching unconditionally keeps the header honest
      // without re-deriving the server's funding rule here — a second copy of
      // that rule is exactly how the two sides drift apart.
      userManager.invalidate();

      // Admin-like viewers (Admin and its deputy, SubAdmin) see the whole tree,
      // so both need the affected list refetched.
      const isAdminLike =
        user.role === UserRole.Admin || user.role === UserRole.SubAdmin;

      if (
        (user.role === UserRole.Dealer || isAdminLike) &&
        type === "subDealers"
      ) {
        queryClient.invalidateQueries({
          queryKey: [SubDealersQueryKeys.All],
        });
      }

      if (isAdminLike && type === "mainDealers") {
        queryClient.invalidateQueries({
          queryKey: [MainDealersQueryKeys.All],
        });
      }

      if (type === "players") {
        queryClient.invalidateQueries({
          queryKey: [SubDealersQueryKeys.Players],
        });
      }
      if (type === "agents") {
        queryClient.invalidateQueries({
          queryKey: [AgentsQueryKeys.All],
        });
      }
    },
  });
};
